/**
 * La ÚNICA llamada admin real que expone hoy `apps/api/src/suscripcion/` —
 * `reconciliacion.controller.ts` (CA-02.2/CA-02.3).
 *
 * 🔴 **No existe un `GET` admin que liste conductores con su estado de suscripción.** Barrido
 * completo del módulo antes de escribir esto: `suscripcion.controller.ts` sólo expone
 * `GET /conductores/yo/suscripcion` y es del lado CONDUCTOR (usa `req.userId` del propio token,
 * no admite `:conductorId`); `conductores.controller.ts` (otro módulo) tampoco tiene ninguna ruta
 * con `:conductorId` — todas sus rutas son `yo/...`, sobre la cuenta propia. El único cruce
 * conductor↔suscripción que SÍ trae un admin es `GET /consola/verificacion/conductores/:id`
 * (`verificacionApi.ts` → `obtenerDetalle`), que devuelve `ConductorDetalle.suscripcion` — pero
 * exige conocer de antemano el id de un conductor puntual, no lista.
 *
 * ⇒ `Suscripciones.tsx` NO puede hoy reemplazar su tabla mock (todos los conductores con
 * suscripción) por una real: el endpoint que la alimentaría no existe. Lo que SÍ existe y está
 * cableado acá es la reconciliación manual de UN conductor — la misma acción que hoy el mock
 * muestra como botón "Volver a consultar" deshabilitado.
 *
 * ⚠️ **Verificado por efecto: en este ambiente de desarrollo, `reconciliar` SIEMPRE devuelve
 * `{reconciliado:false, suscripcion:null}`.** `SuscripcionModule` usa `StubClienteHubPagos`
 * (`suscripcion.module.ts`, el `useClass` de `CLIENTE_HUB_PAGOS`) mientras no exista la
 * merchant-app de taxi en hub-pagos — el stub devuelve `null` incondicionalmente (con un
 * `Logger.warn` en el proceso del backend) para CUALQUIER conductorId, exista o no. Probado con
 * un id real y con `00000000-0000-0000-0000-000000000000`: mismo resultado en los dos casos. La
 * pantalla trata `reconciliado:false` como respuesta VÁLIDA ("hub-pagos no tiene nada que
 * reportar hoy"), no como error — es exactamente lo que dice `ReconciliacionService.
 * reconciliarUno`.
 */
import { postear } from './clienteApi';
import type { SuscripcionReal } from './verificacionApi';

export type { SuscripcionReal };

export interface ResultadoReconciliacion {
  reconciliado: boolean;
  suscripcion: SuscripcionReal | null;
}

/**
 * POST /suscripcion/:conductorId/reconciliar (`ReconciliacionController`, sólo operador/admin —
 * `OperadorGuard`, verificado por efecto: 403/`operador_requerido` con un token sin
 * `esAdminPlataforma`). Rechaza con **503** si la CONSULTA a hub-pagos falla de verdad (red/5xx),
 * a diferencia de "no tiene nada" — ver el comentario de `ReconciliacionService.reconciliarUno`.
 * `ErrorApi` ya trae ese mensaje tal cual lo arma el backend, listo para mostrar.
 */
export const reconciliarSuscripcion = (conductorId: string): Promise<ResultadoReconciliacion> =>
  postear<ResultadoReconciliacion>(`/suscripcion/${conductorId}/reconciliar`);
